import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Sparkles, Filter } from "lucide-react";
import { INFOGRAPHICS, CATEGORIES } from "@/data/infographics";

const CAT_TONE = {
  Overview: "border-slate-300 bg-slate-100 text-slate-800",
  Build:    "border-sky-300 bg-sky-50 text-sky-800",
  Grow:     "border-orange-300 bg-orange-50 text-orange-800",
  Serve:    "border-violet-300 bg-violet-50 text-violet-800",
  Measure:  "border-emerald-300 bg-emerald-50 text-emerald-800",
};

export default function InfographicsGallery() {
  const [cat, setCat] = useState("All");

  useEffect(() => {
    document.title = "Feature Gallery — WebsiteTech.pro";
  }, []);

  const visible = cat === "All"
    ? INFOGRAPHICS
    : INFOGRAPHICS.filter((i) => i.category === cat);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="infographics-gallery">
      <Navbar />

      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-16">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
            <Sparkles className="h-4 w-4" /> WebsiteTech · Feature Gallery
          </div>
          <h1 className="mt-4 font-heading text-4xl font-black leading-tight text-slate-900 sm:text-5xl lg:text-6xl">
            Every capability, <span className="text-orange-600">one picture at a time.</span>
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-600 sm:text-lg">
            {INFOGRAPHICS.length} visual explainers of what WebsiteTech does — grouped
            by the outcome each one delivers. Tap any card to open the live
            feature page.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            {["All", ...CATEGORIES].map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition ${cat === c ? "border-orange-400 bg-orange-50 text-orange-800 ring-2 ring-offset-1 ring-orange-300" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                data-testid={`gallery-filter-${c.toLowerCase()}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12">
        <div className="grid gap-6 md:grid-cols-2">
          {visible.map((info) => (
            <Link
              key={info.slug}
              to={info.featurePath}
              className="group block overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-xl"
              data-testid={`gallery-item-${info.slug}`}
            >
              <div className="relative">
                <img
                  src={info.url}
                  alt={`${info.title} — ${info.tagline}`}
                  loading="lazy"
                  className="block h-auto w-full"
                />
                {info.comingSoon && (
                  <Badge className="absolute right-3 top-3 bg-amber-500 text-white">
                    Coming soon
                  </Badge>
                )}
              </div>
              <div className="border-t border-slate-100 p-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Badge variant="outline" className={`text-[10px] uppercase ${CAT_TONE[info.category] || CAT_TONE.Overview}`}>
                    {info.category}
                  </Badge>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-orange-600 transition group-hover:gap-2">
                    {info.featureLabel} <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
                <div className="mt-3 font-heading text-xl font-bold text-slate-900">
                  {info.title}
                </div>
                <p className="mt-1 text-sm text-slate-600">{info.tagline}</p>
              </div>
            </Link>
          ))}
        </div>

        {visible.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">
            No infographics in this category yet.
          </div>
        )}
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-16">
        <div className="rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-sky-50 p-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="font-heading text-2xl font-black text-slate-900">
                Ready to pick a package?
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                Four outcome-based tiers · month-to-month · you own everything.
              </p>
            </div>
            <Link
              to="/packages"
              className="inline-flex items-center gap-2 rounded-full bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange-700"
              data-testid="gallery-packages-cta"
            >
              See packages <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
